import get from 'lodash/get'
import range from 'lodash/range'

import type { Playlist } from 'types/playlist'
import type { Track } from 'types/track'
import type { Brief } from 'types/brief'

import { BRIEF, PLAYLIST, TRACK } from '@disco/constants/ItemTypes'
import {
  INDIVIDUAL_ITEM_SELECTION,
  RANGE_SELECTION,
} from '@disco/constants/SelectionTypes'
import { SelectableItem } from '@disco/types/types/selection'

/**
 * Checks if a dragged item is part of the current selection.
 *
 * Determines whether the item being dragged is already included in the
 * selected items array. Useful for drag and drop operations to know
 * if the entire selection should be moved or just the single item.
 *
 * @param selectedItems - Array of currently selected tracks, playlists, and briefs
 * @param draggedItem - Object containing the track, playlist, or brief being dragged
 * @returns The selected item if found, null/undefined otherwise
 *
 * @example
 * ```typescript
 * const selected = [
 *   { type: 'track', id: '123' },
 *   { type: 'playlist', playlist_id: '456' },
 *   { type: 'brief', id: '789' }
 * ]
 *
 * const dragged = { track: { id: '123', type: 'track' } }
 * const isSelected = isDraggedItemPartOfSelection(selected, dragged)
 * // Returns the track object since it's in the selection
 *
 * const notSelected = { track: { id: '999', type: 'track' } }
 * const notFound = isDraggedItemPartOfSelection(selected, notSelected)
 * // Returns undefined since track 999 is not selected
 * ```
 */
export function isDraggedItemPartOfSelection(
  selectedItems: SelectableItem[],
  draggedItem: {
    track?: Track
    playlist?: Playlist
    brief?: Brief
  },
): SelectableItem | null | undefined {
  const result = selectedItems.find(item => {
    const isTrackSelected =
      draggedItem.track &&
      item.type === TRACK &&
      item.id === draggedItem.track.id

    const isPlaylistSelected =
      draggedItem.playlist &&
      item.type === PLAYLIST &&
      item.playlist_id === draggedItem.playlist.playlist_id

    const isBriefSelected =
      draggedItem.brief &&
      item.type === BRIEF &&
      item.id === draggedItem.brief.id

    return isTrackSelected || isPlaylistSelected || isBriefSelected
  })

  return result
}

/**
 * Handles item selection based on modifier keys pressed during mouse events.
 *
 * Implements standard selection behavior:
 * - Cmd/Ctrl: Individual item selection (toggle)
 * - Shift: Range selection from last selected item
 * - No modifiers: Individual selection (if allowed)
 *
 * @param e - Mouse event containing modifier key information
 * @param props - Props object containing selection state and handlers
 */
export function handleModifierKeysSelection(e: MouseEvent, props: any) {
  // if no modifier keys is used, in shortlists it should still behave like
  // individual item select
  if (e.metaKey || e.ctrlKey || props.indivSelectAllowed) {
    props.selectItems(
      props.selectionId,
      [props.index],
      INDIVIDUAL_ITEM_SELECTION,
    )
  } else if (e.shiftKey) {
    const foldersLength = get(props.pane, 'folders.length', 0)
    // TODO: refactor activeItem splitting
    const activeItemIndex = props.pane?.activeItem
      ? Number(props.pane.activeItem.split('/')[1])
      : 0

    const defaultSelectionStartMarker =
      activeItemIndex >= foldersLength ? activeItemIndex : foldersLength

    const selectionMarker =
      get(props.selection?.[props.selectionId], 'selectionStartMarker', 0) ||
      defaultSelectionStartMarker

    let selectionRange =
      props.index > selectionMarker
        ? range(selectionMarker, props.index + 1)
        : range(props.index, selectionMarker + 1)

    // Filter out excluded item types
    if (props.excludeItemTypes && props.excludeItemTypes.length) {
      selectionRange = selectionRange.filter(index => {
        const item = props.pane.results[index]
        return item && !props.excludeItemTypes.includes(item.type)
      })
    }

    props.selectItems(props.selectionId, selectionRange, RANGE_SELECTION)
  }
}

export function handleModifierKeysDataTableSelection(
  e: Pick<MouseEvent, 'shiftKey' | 'metaKey' | 'ctrlKey'>,
  props: {
    index: number
    selectionId: string
    selectItems: (id: string, range: number[], type: string) => void
    selection: Record<string, { selectionStartMarker?: number } | null>
    lastRowSelected: number | null
  },
) {
  const { index, selectionId, selectItems, selection, lastRowSelected } = props
  if (e.shiftKey) {
    const selectionMarker =
      lastRowSelected ??
      get(selection[selectionId], 'selectionStartMarker', index)
    const selectionRange =
      index > selectionMarker
        ? range(selectionMarker, index + 1)
        : range(index, selectionMarker + 1)
    selectItems(selectionId, selectionRange, RANGE_SELECTION)
    return
  }
  selectItems(selectionId, [index], INDIVIDUAL_ITEM_SELECTION)
}

/**
 * Checks if the selection contains any nested tracks.
 *
 * Determines whether any of the selected tracks have a nesting parent,
 * indicating they are nested/child tracks. Useful for UI logic that
 * needs to handle nested tracks differently.
 *
 * @param items - Array of selected items (tracks and playlists)
 * @returns True if any track in the selection has a nesting parent
 */
export const selectionContainNestedTracks = (items: any[] = []): boolean => {
  const tracks = items.filter(item => item.type === TRACK)

  return tracks.some(track => Boolean(track?.nesting_parent_id))
}
